import { BeneficialOwnerControlType } from '@commenda/api';
import { SingleCombobox } from '@commenda/kakashi';
import { CONTROL_TYPE_CAPTIONS, CONTROL_TYPE_OPTIONS } from '../constants';

interface ControlTypePickerProps {
  value: BeneficialOwnerControlType | undefined;
  onChange: (value: BeneficialOwnerControlType) => void;
  placeholder?: string;
}

/**
 * Shared `SingleCombobox` configured for the UBO control-type picker —
 * label + caption per option, no search, active-row highlighted. Used by
 * the per-stakeholder section, the inheritable-UBO list, and the
 * standalone "Edit control type" modal.
 *
 * `SingleCombobox` is string-typed; we narrow at the boundary here so
 * callers receive a typed `BeneficialOwnerControlType` and never cast.
 * Runtime is safe: the combobox only emits values from
 * `CONTROL_TYPE_OPTIONS`, which is statically typed to the enum.
 */
export const ControlTypePicker = ({
  value,
  onChange,
  placeholder = 'Select control type',
}: ControlTypePickerProps) => (
  <SingleCombobox
    options={CONTROL_TYPE_OPTIONS}
    value={value ?? ''}
    setValue={(v) => onChange(v as BeneficialOwnerControlType)}
    placeholder={placeholder}
    hideSearch
    canClearSelection={false}
    selectedItemClassName="bg-bg-active-light"
    renderOption={({ option, onSelect }) => (
      <button
        type="button"
        onClick={() => onSelect(option.value)}
        className="flex w-full flex-col gap-xxs text-left"
      >
        <span className="txt-small-medium text-text-primary">
          {option.label}
        </span>
        <span className="txt-extra-small-regular text-text-tertiary whitespace-normal">
          {CONTROL_TYPE_CAPTIONS[option.value]}
        </span>
      </button>
    )}
  />
);
